import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { todayLocalKey } from '@/utils/format';
import { getClosureScheduleNote } from '@/utils/trades/closure-schedule';
import type { HolidayItem, TradeItem } from '@/types/trades.types';

interface SetCloseDateDialogProps {
  /** The trade whose close date is being stamped; `null` keeps the dialog shut. */
  trade: TradeItem | null;
  onClose: () => void;
  date: string;
  onDateChange: (value: string) => void;
  reason: string;
  onReasonChange: (value: string) => void;
  /** Full holidays list from `useHolidays`; matched locally against `date`. */
  holidays?: HolidayItem[];
  isLoading: boolean;
  error?: Error | null;
  onConfirm: () => void;
}

/**
 * Blotter Set-Close-Date dialog (LR-576 rename — it stamps the CLOSE settlement
 * date, after the LR-406 blotter column).
 *
 * Lifted out of TradesPage so it can be rendered on its own: as inline JSX in a
 * 1400-line page the only way to reach it was a full-page harness, and that is
 * how it came to be the dialog that quietly missed the LR-610 note its
 * detail-page twin had. Purely presentational — the page still owns the picked
 * date, the reason and the mutation, so behaviour is unchanged by the move.
 *
 * Still a separate component from the detail page's `SettlementDateModal`: that
 * one holds its own state and adds a before→after preview. Converging them is
 * worth doing, but it is a behaviour change for this dialog, not a refactor.
 */
export function SetCloseDateDialog({
  trade,
  onClose,
  date,
  onDateChange,
  reason,
  onReasonChange,
  holidays,
  isLoading,
  error,
  onConfirm,
}: SetCloseDateDialogProps) {
  // `is_active` matters: a deactivated holiday is not a holiday, and warning on
  // one would send the trader hunting for another date for no reason. Mirrors
  // the same filter in SettlementDateModal.
  const matchedHoliday = holidays?.find((h) => h.is_active && h.date === date);

  // LR-610: on an `active` trade the close date is a plan, not a settlement —
  // the shared rule, so this dialog and SettlementDateModal say the same thing.
  const scheduleNote = getClosureScheduleNote(trade, date);

  // LR-412: min = max(today, open_settlement_date). Mirrors backend
  // `Position.set_settlement_date` guards: no past dates, and close >= open.
  // HTML5 form validation gives the operator immediate feedback before the
  // server round-trip.
  //
  // `todayLocalKey()`, not the UTC slice: this is a wire date the trader types,
  // and @/utils/format names settlement-date as a local-key case (the UTC slice
  // bumps the floor a day forward from a UTC-negative evening, and back a day in
  // a UTC-positive late evening). The detail-page modal already reads it this
  // way — a floor that disagreed between the two dialogs is how this one drifted.
  //
  // LR-702 adds maturity to that max on an ACTIVE trade whose end is known.
  // Closure runs only on `matured`, so a date before maturity is unexecutable:
  // `effective_close_date` clamps the real settlement up to the maturity date,
  // and the stored value then disagrees with the day the trade actually settles
  // — which is what the blotter column shows. The note below says so, but only
  // while the dialog is open; the floor is what stops the record being wrong.
  // Not applied on `matured`: maturity is already behind, and the today/open
  // floor is the binding one there.
  const minDate = (() => {
    const today = todayLocalKey();
    const candidates = [today];
    const openDate = trade?.settlement_date;
    if (openDate) candidates.push(openDate);
    if (trade?.status === 'active' && trade.maturity_date) {
      candidates.push(trade.maturity_date);
    }
    return candidates.reduce((a, b) => (b > a ? b : a));
  })();

  // Every visible note describes the field, in render order — mirrors the
  // detail-page modal so a SR user hears the same constraints on focus.
  const describedBy = [
    matchedHoliday ? 'settlement-holiday' : null,
    scheduleNote ? 'settlement-schedule' : null,
  ].filter((id): id is string => id !== null).join(' ') || undefined;

  return (
    <ConfirmDialog
      show={!!trade}
      onClose={onClose}
      title="Set Close Date"
      confirmLabel="Confirm"
      isLoading={isLoading}
      // No enabled-but-silent Confirm: a native date input can be cleared to '',
      // and the mutation would then be a guaranteed 400. Matches SettlementDateModal.
      confirmDisabled={!date}
      onConfirm={onConfirm}
      error={error}
      errorFallback="Failed to set close date. Please try again."
    >
      <p className="text-sm text-gray-500">
        Set the actual close settlement date for position <strong>{trade?.id}</strong> ({trade?.symbol_id}).
      </p>
      <div className="mt-4">
        <label htmlFor="actual_settlement_date" className="block text-xs text-gray-500 mb-1">
          Close Settlement Date
        </label>
        <input
          type="date"
          id="actual_settlement_date"
          value={date}
          onChange={(e) => onDateChange(e.target.value)}
          min={minDate}
          aria-describedby={describedBy}
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
        />
        {matchedHoliday && (
          <div
            id="settlement-holiday"
            className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800"
            aria-live="polite"
          >
            {date} is a public holiday{matchedHoliday.name ? `: ${matchedHoliday.name}` : ''}.
            Consider another date.
          </div>
        )}
        {scheduleNote && (
          <div
            id="settlement-schedule"
            className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1 text-xs text-amber-800"
            aria-live="polite"
          >
            {scheduleNote}
          </div>
        )}
      </div>
      <div className="mt-4">
        <label htmlFor="settlement_reason" className="block text-xs text-gray-500 mb-1">
          Reason (optional)
        </label>
        <textarea
          id="settlement_reason"
          value={reason}
          onChange={(e) => onReasonChange(e.target.value)}
          placeholder="e.g. Standard T+3 settlement"
          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
          rows={2}
        />
      </div>
    </ConfirmDialog>
  );
}
